// Sprint runner-picker-session-primary — derive what the runner picker
// renders for each row, from the canonical Anthropic OAuth usage
// payload that the "Runner usage" modal already shows.
//
// Policy (spec §2 D1/D2):
//
//   * Primary "% libre" big number = `100 - session_5h_used`. This is
//     the bucket that actually limits what an agent created RIGHT NOW
//     can do over the next few hours; matching the founder's intuition
//     for "can this runner serve my agent now?" is the goal.
//
//   * Secondary subtitle line = `Weekly X % libre · Sonnet Y % libre`,
//     in that order, dropping any bucket whose payload is null. Same
//     unit ("libre") as the primary so a 2-second glance does not need
//     a mental conversion.
//
// The previous helper (`mostRestrictiveFreePct`, sprint
// `runner-picker-canonical-usage`) took the max across all three
// buckets — that mixed two horizons in a single number and produced
// counter-intuitive picks like "R1 at 39 % libre" when session was at
// 100 % free. This file is the structural replacement; the old name
// is gone (P6: edit > extend > new, single caller).
//
// `seven_day_opus` continues to be excluded — same rationale as the
// prior sprint, and unchanged by Santi.
//
// Failure path: if the whole `usage` payload is null OR the session
// bucket is null, the helper returns `null` and the modal renders the
// existing `capacityUnknown` copy. No primary → no subtitle either;
// without context the buckets cannot be qualified.

import type { UsageData } from './api';

// `'weekly'` and `'sonnet'` remain for legacy-payload compatibility.
// `'weekly_scoped'` is the v10 shape — `modelName` carries the
// display name Anthropic reports (e.g. "Fable"), which the picker
// renders as "Weekly · Fable" in the founder-mode subtitle.
export type SecondaryBucketLabel = 'weekly' | 'sonnet' | 'weekly_scoped';

export interface SecondaryBucket {
  label: SecondaryBucketLabel;
  freePct: number;
  modelName?: string;
}

export interface CapacityBreakdown {
  primaryFreePct: number;
  secondary: SecondaryBucket[];
}

function freePct(utilization: number): number {
  // Clamp to [0, 100]: a runner reporting >100% used would otherwise
  // render a negative bar, and a (theoretical) negative utilization
  // would render >100% free. Anthropic's payload is well-behaved
  // today but the bar is per-render arithmetic.
  return Math.max(0, Math.min(100, Math.round(100 - utilization)));
}

// Sprint usage-limits-v2 (2026-07-08) — canonical `limits[]` array.
// Primary stays session (D7 — matches the founder's "can this runner
// serve my agent NOW?" mental model). Secondary is derived from
// weekly_all + each weekly_scoped so a saturated model-scoped limit
// like Fable at 100 % surfaces in the picker instead of hiding under
// a healthy `seven_day` legacy bucket. Legacy payload (v9 wire, or
// v10 without limits) falls back to the pre-sprint projection (D2).
export function deriveCapacity(usage: UsageData | null): CapacityBreakdown | null {
  if (!usage) return null;

  if (usage.limits && usage.limits.length > 0) {
    // Session primary comes from the `session` entry in limits[].
    // If Anthropic omits `session` we fall through to the legacy
    // five_hour bucket so the picker still renders.
    const sessionEntry = usage.limits.find((e) => e.kind === 'session');
    if (!sessionEntry && !usage.five_hour) return null;
    const primaryPct = sessionEntry ? sessionEntry.percent : usage.five_hour!.utilization;
    const secondary: SecondaryBucket[] = [];
    for (const entry of usage.limits) {
      if (entry.kind === 'weekly_all') {
        secondary.push({ label: 'weekly', freePct: freePct(entry.percent) });
      } else if (entry.kind === 'weekly_scoped') {
        secondary.push({
          label: 'weekly_scoped',
          freePct: freePct(entry.percent),
          modelName: entry.modelName,
        });
      }
      // Unknown kinds are skipped from the picker subtitle — the modal
      // still surfaces them via LimitsBars for founder visibility.
    }
    return {
      primaryFreePct: freePct(primaryPct),
      secondary,
    };
  }

  // Legacy (v9 wire / limits absent).
  const session = usage.five_hour;
  if (!session) return null;
  const secondary: SecondaryBucket[] = [];
  if (usage.seven_day) {
    secondary.push({ label: 'weekly', freePct: freePct(usage.seven_day.utilization) });
  }
  if (usage.seven_day_sonnet) {
    secondary.push({ label: 'sonnet', freePct: freePct(usage.seven_day_sonnet.utilization) });
  }
  return {
    primaryFreePct: freePct(session.utilization),
    secondary,
  };
}
